import { Link } from "react-router-dom";
import { Icon, IconGlyph } from "./Icon";

type TabKey = "kalender" | "boder" | "finans" | "lod" | "admin";

const TAB_META: Record<TabKey, { label: string; icon: IconGlyph; to: string }> = {
  kalender: { label: "Kalender", icon: "calendar-days", to: "/" },
  boder: { label: "Bøder", icon: "coins", to: "/penalties" },
  finans: { label: "Finans", icon: "chart-line", to: "/finance" },
  lod: { label: "Spil", icon: "dice", to: "/lod" },
  admin: { label: "Admin", icon: "sliders", to: "/edit" },
};

export function TabBar({
  active,
  admin = false,
  unpaidFinesCount = 0,
  tone = "paper",
}: {
  active: TabKey;
  admin?: boolean;
  unpaidFinesCount?: number;
  /** `vault` repaints the bar for the finance area's dark ground. */
  tone?: "paper" | "vault";
}) {
  const tabs: TabKey[] = admin
    ? ["kalender", "boder", "finans", "lod", "admin"]
    : ["kalender", "boder", "finans", "lod"];
  const vault = tone === "vault";
  return (
    <div
      // Vertical sizing lives in index.css (.dss-tab-bar): it depends on
      // whether the app is installed, which is more than a class name can say.
      className={`dss-tab-bar border-t grid gap-1.5 flex-shrink-0 ${
        vault ? "dss-tab-bar-vault" : "border-line bg-paper"
      } ${
        // Five tabs on a narrow phone need every pixel between them; four
        // still look better with the original margin.
        tabs.length > 4 ? "px-2" : "px-3.5"
      }`}
      style={{ gridTemplateColumns: `repeat(${tabs.length}, 1fr)` }}
    >
      {tabs.map((k) => {
        const on = active === k;
        const meta = TAB_META[k];
        const badge = k === "boder" ? unpaidFinesCount : 0;
        return (
          <Link
            key={k}
            to={meta.to}
            className="dss-tab-item flex flex-col items-center justify-center gap-1"
          >
            {/* No pill: a box around the icon read as oversized next to a
                two-tab bar, narrower than the label under a longer one — and
                iOS's own tab bar doesn't use one either. The icon swapping to
                its filled variant, plus this transition on colour and scale,
                is what carries the selected state instead. */}
            <div className="relative flex">
              <Icon
                name={meta.icon}
                variant={on ? "solid" : "regular"}
                size={19}
                className={`transition-[color,transform] duration-200 ease-out ${
                  on
                    ? `${vault ? "text-vault-ink" : "text-ink"} scale-110`
                    : `${vault ? "text-vault-ink3" : "text-ink3"} scale-100`
                }`}
              />
              {badge > 0 && (
                <div
                  className={`absolute -top-1 -right-2.5 min-w-[16px] h-4 px-1 rounded-full font-mono text-[9.5px] font-bold flex items-center justify-center ${
                    vault ? "bg-ember text-vault" : "bg-clay text-paper"
                  }`}
                  style={{
                    boxShadow: `0 0 0 2px ${
                      vault ? "oklch(0.185 0.016 152)" : "var(--color-paper)"
                    }`,
                  }}
                >
                  {badge}
                </div>
              )}
            </div>
            <div
              className={`font-mono ${
                // Five columns leaves "Kalender" about 62px to sit in, and
                // the wide tracking is the first thing that has to give —
                // otherwise the longest label wraps or clips.
                tabs.length > 4 ? "text-[9px] tracking-[1.1px]" : "text-[9.5px] tracking-[1.5px]"
              } uppercase transition-colors duration-200 ease-out ${
                on
                  ? `${vault ? "text-vault-ink" : "text-ink"} font-bold`
                  : `${vault ? "text-vault-ink3" : "text-ink3"} font-medium`
              }`}
            >
              {meta.label}
            </div>
          </Link>
        );
      })}
    </div>
  );
}
